<template>
  <div class="datepicker" :class="position">
    <!-- Calendar Button -->
    <button
      id="date-picker-button"
      type="button"
      class="subtle icon before"
      :class="{ 'active' : isOpen }"
      :disabled="disabled"
      @click="showCalendar"
    >
      <span class="calendar-icon" @click="showCalendar">
        <svgicon name="calendar" :alt="$t('calendar')" />
      </span>
      {{ formattedValue }}
    </button>

    <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
      <span v-show="isOpen" class="context-bg" @click="close" />
    </transition>
    <!-- Day View -->
    <div v-if="showDayView" class="calendar days">
      <header>
        <button
          class="prev"
          :disabled="previousMonthDisabled(pageTimestamp)"
          @click="previousMonth()"
        >
          <svgicon type="button" class="flat" name="arrow-left" :alt="$t('previous month')" />
        </button>
        <button
          type="button"
          class="large flat calendar-title"
          @click="showMonthCalendar"
        >
          {{ currMonthName }} {{ currYear }}
        </button>
        <button class="next" :disabled="nextMonthDisabled(pageTimestamp)" @click="nextMonth()">
          <svgicon type="button" class="flat" name="arrow-right" :alt="$t('next month')" />
        </button>
      </header>
      <section>
        <span v-for="d in dayNames" :key="d.timestamp" class="cell day-header">{{ d }}</span>
        <span v-for="i in blankDays" :key="i" class="cell day blank">&nbsp;</span>
        <span v-for="day in days" :key="day.timestamp" class="cell day">
          <button type="button" class="flat" :class="dayClasses(day)" @click="selectDate(day)">{{ day.date }}</button>
        </span>
      </section>
    </div>

    <!-- Month View -->
    <div v-if="showMonthView" class="calendar months">
      <header>
        <button
          class="prev"
          :disabled="previousYearDisabled(pageTimestamp)"
          @click="previousYear"
        >
          <svgicon type="button" class="flat" name="arrow-left" :alt="$t('previous year')" />
        </button>
        <button
          type="button"
          class="large flat calendar-title"
          @click="showYearCalendar"
        >
          {{ getPageYear() }}
        </button>
        <button class="next" :disabled="nextYearDisabled(pageTimestamp) " @click="nextYear">
          <svgicon type="button" class="flat" name="arrow-right" :alt="$t('next year')" />
        </button>
      </header>
      <section>
        <span v-for="m in months" :key="m.timestamp" class="cell month">
          <button
            type="button"
            class="flat"
            :class="{ 'selected': m.isSelected, 'disabled': m.isDisabled }"
            @click.stop="selectMonth(m)"
          >
            {{ m.month }}
          </button>
        </span>
      </section>
    </div>

    <!-- Year View -->
    <div v-if="showYearView" class="calendar years">
      <header>
        <button
          class="prev"
          :disabled="previousDecadeDisabled(pageTimestamp)"
          @click="previousDecade"
        >
          <svgicon type="button" class="flat" name="arrow-left" :alt="$t('previous decade')" />
        </button>
        <button
          type="button"
          class="large flat calendar-title"
        >
          {{ getPageDecade() }}
        </button>
        <button class="next" :disabled="nextMonthDisabled(pageTimestamp)" @click="nextDecade">
          <svgicon type="button" class="flat" name="arrow-right" :alt="$t('next decade')" />
        </button>
      </header>
      <section>
        <span v-for="year in years" :key="year.timestamp" class="cell year">
          <button
            type="button"
            class="flat"
            :class="{ 'selected': year.isSelected, 'disabled': year.isDisabled }"
            @click.stop="selectYear(year)"
          >
            {{ year.year }}
          </button>
        </span>
      </section>
    </div>
  </div>
</template>

<script>
import '@/lib/icons/calendar';
import '@/lib/icons/arrow-left';
import '@/lib/icons/arrow-right';
import moment from 'moment';

export default {
  props: {
    value: {
      type: [Date, String],
      default: null,
    },
    format: {
      type: String,
      default: 'MMM YYYY',
    },
    openDate: {
      type: [Date, String],
      default: Date,
    },
    enabledDates: {
      type: Object,
      default: Object,
    },
    highlighted: {
      type: Object,
      default: Object,
    },
    initialView: {
      type: String,
      default: 'day',
    },
    minimumView: {
      type: String,
      default: 'day',
    },
    maximumView: {
      type: String,
      default: 'year',
    },
    position: {
      type: String,
      default: 'left',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /*
       * Vue cannot observe changes to a Date Object so date must be stored as a timestamp
       * This represents the first day of the current viewing month
       * {Number}
       */
      pageTimestamp: new Date(this.openDate).setDate(1),
      selectedDate: null,
      // Flags to show calendar views {Boolean}
      showDayView: false,
      showMonthView: false,
      showYearView: false,
    };
  },
  computed: {
    // Month and day name arrays
    monthNames() {
      return moment.monthsShort();
    },
    dayNames() {
      return moment.weekdaysShort();
    },
    computedInitialView() {
      if (this.allowedToShowView(this.initialView)) return this.initialView;
      return this.minimumView;
    },
    pageDate() {
      return new Date(this.pageTimestamp);
    },
    formattedValue() {
      if (!this.selectedDate) {
        return null;
      }
      const timezoneCorrection = new Date().getTimezoneOffset();
      return moment(this.selectedDate)
        .utcOffset(timezoneCorrection)
        .format(this.format);
    },
    currMonthName() {
      return this.monthNames[this.pageDate.getMonth()];
    },
    currYear() {
      return this.pageDate.getFullYear();
    },
    /**
     * Returns an empty array representing the empty cells before the first day in
     * the calendar layout.
     * @return {Array}
     */
    blankDays() {
      const d = this.pageDate;
      return Math.max(d.getDay() - 1, 0);
    },
    days() {
      const d = this.pageDate;
      const days = [];
      // set up a new date object to the beginning of the current 'page'
      const dObj = new Date(
        d.getFullYear(),
        d.getMonth(),
        1,
        d.getHours(),
        d.getMinutes(),
      );
      const monthDays = moment(d).daysInMonth();
      for (let i = 0; i < monthDays; i++) {
        days.push({
          date: dObj.getDate(),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedDate(dObj),
          isDisabled: this.isDisabledDate(dObj),
          isHighlighted: this.isHighlightedDate(dObj),
          isHighlightStart: this.isHighlightStart(dObj),
          isHighlightEnd: this.isHighlightEnd(dObj),
          isToday: dObj.toDateString() === new Date().toDateString(),
          isWeekend: dObj.getDay() === 0 || dObj.getDay() === 6,
          isSaturday: dObj.getDay() === 6,
          isSunday: dObj.getDay() === 0,
        });
        dObj.setDate(dObj.getDate() + 1);
      }
      return days;
    },

    months() {
      const d = this.pageDate;
      const months = [];
      // set up a new date object to the beginning of the current 'page'
      const dObj = new Date(
        d.getFullYear(),
        0,
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
      );
      for (let i = 0; i < 12; i++) {
        months.push({
          month: this.monthNames[i],
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedMonth(dObj),
          isDisabled: this.isDisabledMonth(dObj),
        });
        dObj.setMonth(dObj.getMonth() + 1);
      }
      return months;
    },

    years() {
      const d = this.pageDate;
      const years = [];
      // set up a new date object to the beginning of the current 'page'
      const dObj = new Date(
        Math.floor(d.getFullYear() / 10) * 10,
        d.getMonth(),
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
      );
      for (let i = 0; i < 10; i++) {
        years.push({
          year: dObj.getFullYear(),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedYear(dObj),
          isDisabled: this.isDisabledYear(dObj),
        });
        dObj.setFullYear(dObj.getFullYear() + 1);
      }
      return years;
    },
    isOpen() {
      return this.showDayView || this.showMonthView || this.showYearView;
    },
  },
  watch: {
    value(value) {
      this.setValue(value);
    },
    openDate() {
      this.setPageDate();
    },
    initialView() {
      this.setInitialView();
    },
  },
  created() {
    moment.locale(this.$lang);
  },
  mounted() {
    if (this.value) {
      this.setValue(this.value);
    }
  },
  methods: {
    /**
     * Close all calendar layers
     */
    close() {
      this.showDayView = false;
      this.showMonthView = false;
      this.showYearView = false;
      this.$emit('closed');
      document.removeEventListener('click', this.clickOutside, false);
    },
    resetDefaultDate() {
      if (this.selectedDate === null) {
        this.setPageDate();
        return;
      }
      this.setPageDate(this.selectedDate);
    },
    /**
     * Effectively a toggle to show/hide the calendar
     * @return {mixed} [description]
     */
    showCalendar() {
      if (this.isOpen) {
        return this.close();
      }
      this.setInitialView();
      this.$emit('opened');
    },
    setInitialView() {
      const initialView = this.computedInitialView;

      switch (initialView) {
        case 'year':
          this.showYearCalendar();
          break;
        case 'month':
          this.showMonthCalendar();
          break;
        default:
          this.showDayCalendar();
          break;
      }
    },
    allowedToShowView(view) {
      const views = ['day', 'month', 'year'];
      const minimumViewIndex = views.indexOf(this.minimumView);
      const maximumViewIndex = views.indexOf(this.maximumView);
      const viewIndex = views.indexOf(view);

      return viewIndex >= minimumViewIndex && viewIndex <= maximumViewIndex;
    },
    showDayCalendar() {
      if (!this.allowedToShowView('day')) return false;
      this.close();
      this.showDayView = true;
      document.addEventListener('click', this.clickOutside, false);
    },
    showMonthCalendar() {
      if (!this.allowedToShowView('month')) return false;
      this.close();
      this.showMonthView = true;
      document.addEventListener('click', this.clickOutside, false);
    },
    showYearCalendar() {
      if (!this.allowedToShowView('year')) return false;
      this.close();
      this.showYearView = true;
      document.addEventListener('click', this.clickOutside, false);
    },
    setDate(timestamp) {
      const date = new Date(timestamp);
      this.selectedDate = new Date(date);
      this.setPageDate(date);
      this.$emit('selected', new Date(date));
      this.$emit('input', new Date(date));
    },

    /**
     * @param {Object} day
     */
    selectDate(day) {
      if (day.isDisabled) {
        this.$emit('selectedDisabled', day);
        return false;
      }
      this.setDate(day.timestamp);
      this.close();
    },
    /**
     * @param {Object} month
     */
    selectMonth(month) {
      if (month.isDisabled) {
        return false;
      }

      const date = new Date(month.timestamp);
      if (this.allowedToShowView('day')) {
        this.setPageDate(date);
        this.$emit('changedMonth', month);
        this.showDayCalendar();
      } else {
        this.setDate(date);
        this.close();
      }
    },
    /**
     * @param {Object} year
     */
    selectYear(year) {
      if (year.isDisabled) {
        return false;
      }

      const date = new Date(year.timestamp);
      if (this.allowedToShowView('month')) {
        this.setPageDate(date);
        this.$emit('changedYear', year);
        this.showMonthCalendar();
      } else {
        this.setDate(date);
        this.close();
      }
    },
    /**
     * @return {Number}
     */
    getPageDate() {
      return this.pageDate.getDate();
    },
    /**
     * @return {Number}
     */
    getPageMonth() {
      return this.pageDate.getMonth();
    },
    /**
     * @return {Number}
     */
    getPageYear() {
      return this.pageDate.getFullYear();
    },
    /**
     * @return {String}
     */
    getPageDecade() {
      const sD = Math.floor(this.pageDate.getFullYear() / 10) * 10;
      return `${sD}'s`;
    },
    changeMonth(incrementBy) {
      const date = this.pageDate;
      date.setMonth(date.getMonth() + incrementBy);
      this.setPageDate(date);
      this.$emit('changedMonth', date);
    },
    previousMonth() {
      if (!this.previousMonthDisabled()) {
        this.changeMonth(-1);
      }
    },
    previousMonthDisabled() {
      if (!this.enabledDates || !this.enabledDates.to) {
        return false;
      }
      const d = this.pageDate;
      return (
        this.enabledDates.to.getMonth() >= d.getMonth()
        && this.enabledDates.to.getFullYear() >= d.getFullYear()
      );
    },
    nextMonth() {
      if (!this.nextMonthDisabled()) {
        this.changeMonth(+1);
      }
    },
    nextMonthDisabled() {
      if (!this.enabledDates || !this.enabledDates.from) {
        return false;
      }
      const d = this.pageDate;
      return (
        this.enabledDates.from.getMonth() <= d.getMonth()
        && this.enabledDates.from.getFullYear() <= d.getFullYear()
      );
    },
    changeYear(incrementBy, emit = 'changedYear') {
      const date = this.pageDate;
      date.setYear(date.getFullYear() + incrementBy);
      this.setPageDate(date);
      this.$emit(emit);
    },
    previousYear() {
      if (!this.previousYearDisabled()) {
        this.changeYear(-1);
      }
    },
    previousYearDisabled() {
      if (!this.enabledDates || !this.enabledDates.to) {
        return false;
      }
      return this.enabledDates.to.getFullYear() >= this.pageDate.getFullYear();
    },
    nextYear() {
      if (!this.nextYearDisabled()) {
        this.changeYear(1);
      }
    },
    nextYearDisabled() {
      if (!this.enabledDates || !this.enabledDates.from) {
        return false;
      }
      return (
        this.enabledDates.from.getFullYear() <= this.pageDate.getFullYear()
      );
    },
    previousDecade() {
      if (!this.previousDecadeDisabled()) {
        this.changeYear(-10, 'changeDecade');
      }
    },
    previousDecadeDisabled() {
      if (!this.enabledDates || !this.enabledDates.to) {
        return false;
      }
      return (
        Math.floor(this.enabledDates.to.getFullYear() / 10) * 10
        >= Math.floor(this.pageDate.getFullYear() / 10) * 10
      );
    },
    nextDecade() {
      if (!this.nextDecadeDisabled()) {
        this.changeYear(10, 'changeDecade');
      }
    },
    nextDecadeDisabled() {
      if (!this.enabledDates || !this.enabledDates.from) {
        return false;
      }
      return (
        Math.ceil(this.enabledDates.from.getFullYear() / 10) * 10
        <= Math.ceil(this.pageDate.getFullYear() / 10) * 10
      );
    },
    /**
     * Whether a day is selected
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedDate(dObj) {
      return (
        this.selectedDate
        && this.selectedDate.toDateString() === dObj.toDateString()
      );
    },
    /**
     * Whether a day is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledDate(date) {
      let disabled = false;

      if (typeof this.enabledDates === 'undefined') {
        return false;
      }

      if (typeof this.enabledDates.dates !== 'undefined') {
        this.enabledDates.dates.forEach(d => {
          if (date.toDateString() === d.toDateString()) {
            disabled = true;
            return true;
          }
        });
      }
      if (
        typeof this.enabledDates.to !== 'undefined'
        && this.enabledDates.to
        && date < this.enabledDates.to
      ) {
        disabled = true;
      }
      if (
        typeof this.enabledDates.from !== 'undefined'
        && this.enabledDates.from
        && date > this.enabledDates.from
      ) {
        disabled = true;
      }
      if (typeof this.enabledDates.ranges !== 'undefined') {
        this.enabledDates.ranges.forEach(range => {
          if (
            typeof range.from !== 'undefined'
            && range.from
            && typeof range.to !== 'undefined'
            && range.to
          ) {
            if (date < range.to && date > range.from) {
              disabled = true;
              return true;
            }
          }
        });
      }
      if (
        typeof this.enabledDates.days !== 'undefined'
        && this.enabledDates.days.indexOf(date.getDay()) !== -1
      ) {
        disabled = true;
      }
      if (
        typeof this.enabledDates.daysOfMonth !== 'undefined'
        && this.enabledDates.daysOfMonth.indexOf(date.getDate()) !== -1
      ) {
        disabled = true;
      }
      if (
        typeof this.enabledDates.customPredictor === 'function'
        && this.enabledDates.customPredictor(date)
      ) {
        disabled = true;
      }
      return disabled;
    },
    /**
     * Whether a day is highlighted (only if it is not disabled already)
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightedDate(date) {
      if (this.isDisabledDate(date)) {
        return false;
      }

      let highlighted = false;

      if (typeof this.highlighted === 'undefined') {
        return false;
      }

      if (typeof this.highlighted.dates !== 'undefined') {
        this.highlighted.dates.forEach(d => {
          if (date.toDateString() === d.toDateString()) {
            highlighted = true;
            return true;
          }
        });
      }

      if (
        this.isDefined(this.highlighted.from)
        && this.isDefined(this.highlighted.to)
      ) {
        highlighted = date >= this.highlighted.from && date <= this.highlighted.to;
      }

      if (
        typeof this.highlighted.days !== 'undefined'
        && this.highlighted.days.indexOf(date.getDay()) !== -1
      ) {
        highlighted = true;
      }

      if (
        typeof this.highlighted.daysOfMonth !== 'undefined'
        && this.highlighted.daysOfMonth.indexOf(date.getDate()) !== -1
      ) {
        highlighted = true;
      }

      if (
        typeof this.highlighted.customPredictor === 'function'
        && this.highlighted.customPredictor(date)
      ) {
        highlighted = true;
      }

      return highlighted;
    },
    /**
     * Whether a day is highlighted and it is the first date
     * in the highlighted range of dates
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightStart(date) {
      return (
        this.isHighlightedDate(date)
        && this.highlighted.from instanceof Date
        && this.highlighted.from.getFullYear() === date.getFullYear()
        && this.highlighted.from.getMonth() === date.getMonth()
        && this.highlighted.from.getDate() === date.getDate()
      );
    },
    /**
     * Whether a day is highlighted and it is the first date
     * in the highlighted range of dates
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightEnd(date) {
      return (
        this.isHighlightedDate(date)
        && this.highlighted.to instanceof Date
        && this.highlighted.to.getFullYear() === date.getFullYear()
        && this.highlighted.to.getMonth() === date.getMonth()
        && this.highlighted.to.getDate() === date.getDate()
      );
    },
    /**
     * Helper
     * @param  {mixed}  prop
     * @return {Boolean}
     */
    isDefined(prop) {
      return typeof prop !== 'undefined' && prop;
    },
    /**
     * Whether the selected date is in this month
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedMonth(date) {
      return (
        this.selectedDate
        && this.selectedDate.getFullYear() === date.getFullYear()
        && this.selectedDate.getMonth() === date.getMonth()
      );
    },
    /**
     * Whether a month is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledMonth(date) {
      let disabled = false;

      if (typeof this.enabledDates === 'undefined') {
        return false;
      }

      if (typeof this.enabledDates.to !== 'undefined' && this.enabledDates.to) {
        if (
          (date.getMonth() < this.enabledDates.to.getMonth()
            && date.getFullYear() <= this.enabledDates.to.getFullYear())
          || date.getFullYear() < this.enabledDates.to.getFullYear()
        ) {
          disabled = true;
        }
      }
      if (
        typeof this.enabledDates.from !== 'undefined'
        && this.enabledDates.from
      ) {
        if (
          (this.enabledDates.from
            && (date.getMonth() > this.enabledDates.from.getMonth()
              && date.getFullYear() >= this.enabledDates.from.getFullYear()))
          || date.getFullYear() > this.enabledDates.from.getFullYear()
        ) {
          disabled = true;
        }
      }
      return disabled;
    },
    /**
     * Whether the selected date is in this year
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedYear(date) {
      return (
        this.selectedDate
        && this.selectedDate.getFullYear() === date.getFullYear()
      );
    },
    /**
     * Whether a year is disabled
     * @param {Date}
     * @return {Boolean}
     */
    isDisabledYear(date) {
      let disabled = false;
      if (typeof this.enabledDates === 'undefined' || !this.enabledDates) {
        return false;
      }

      if (typeof this.enabledDates.to !== 'undefined' && this.enabledDates.to) {
        if (date.getFullYear() < this.enabledDates.to.getFullYear()) {
          disabled = true;
        }
      }
      if (
        typeof this.enabledDates.from !== 'undefined'
        && this.enabledDates.from
      ) {
        if (date.getFullYear() > this.enabledDates.from.getFullYear()) {
          disabled = true;
        }
      }

      return disabled;
    },
    /**
     * Set the datepicker value
     * @param {Date|String|null} date
     */
    setValue(date) {
      let dateValue = date;
      if (typeof dateValue === 'string') {
        const parsed = new Date(dateValue);
        dateValue = Number.isNaN(parsed.valueOf()) ? null : parsed;
      }
      if (!dateValue) {
        this.setPageDate();
        this.selectedDate = null;
        return;
      }
      this.selectedDate = dateValue;
      this.setPageDate(dateValue);
    },
    setPageDate(date) {
      let dateValue = date;
      if (!dateValue) {
        if (this.openDate) {
          dateValue = new Date(this.openDate);
        } else {
          dateValue = new Date();
        }
      }
      this.pageTimestamp = new Date(dateValue).setDate(1);
    },
    /**
     * Close the calendar if clicked outside the datepicker
     * @param  {Event} event
     */
    clickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.resetDefaultDate();
        this.close();
        document.removeEventListener('click', this.clickOutside, false);
      }
    },
    dayClasses(day) {
      return {
        selected: day.isSelected,
        disabled: day.isDisabled,
        highlighted: day.isHighlighted,
        today: day.isToday,
        weekend: day.isWeekend,
        sat: day.isSaturday,
        sun: day.isSunday,
        'highlight-start': day.isHighlightStart,
        'highlight-end': day.isHighlightEnd,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
header {
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: .50rem;
  border-bottom: 1px solid $neutral-100;

  span {
    color: $pearl-500;
  }

  .calendar-title {
    color: black;
    font-weight: 600;
  }
}
section {
  padding: 1rem;
  text-align: left;
}

.datepicker {
  position: relative;
  display: inline-block;
}

#date-picker-button {
  font-weight: 300;

  .calendar-icon {
    color: $black;
    height: 1rem;
  }
}

.context-bg {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba($primary-300, 0.8);
  z-index: 5;
  @media #{$screen-width-small} {
    display: none;
  }
}

.animated {
  animation-duration: 0.3s;
}

.calendar {
  min-width: 18rem;
  background-color: $white;
  border-radius: 0.25rem;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  margin: .5rem;
  border: 1px solid $pearl-300;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2);

  z-index: 9999999999; // go above intercom
  @media #{$screen-width-small} {
    position: absolute;
    bottom: auto;
    margin: auto;
    margin-top: .25rem;
    max-width: 18rem;

    &.days {
      height: auto;
    }
  }

  .prev,
  .next {
    display: flex;
    font-size: 1.25rem;
    font-weight: 600;
    color: $black;
    &:hover,
    &:focus,
    &:active {
      color: $primary-300;
    }
    &:disabled {
      color: transparent;
    }
  }

  .cell {
    text-align: center;
    display: inline-block;
    color: $black;
    height: 2rem;

    button {
      padding: 0.25rem 0.75rem;
      color: $primary-500;

      &:hover,
      &:focus,
      &:active {
        color: $primary-300;
      }

      &.disabled {
        color: $pearl-500;
        cursor: default;
      }

      &:active,
      &.selected {
        color: $black;
      }
    }

    &.day-header {
      color: $black;
      font-size: 1rem;
      font-weight: 600;
    }

    &.day,
    &.day-header,
    &.day.blank {
      width: calc(100% / 7.1);

      button {
        padding: 0;
        width: 100%;
        height: 100%;
      }
    }

    &.month,
    &.year {
      width: calc(100% / 4.1);
      height: auto;
      padding: 0.25rem;
    }
  }
}

.svg-icon {
  vertical-align: top;
}
</style>
